import type { FieldOrigin } from 'features/libra/ontology/ontologyFieldTypes'
import { useLibraAssetModelData } from 'features/libra/stepComponents/useLibraAssetModelData'
import { applyOriginOption } from 'features/libra/ticket/fieldOrigin'
import { buildFieldValuesPatch } from 'features/libra/ticket/fieldValuesPatch'
import type { LibraTicket } from 'features/libra/ticket/libraTicketsApi'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useTicketWriter } from 'features/libra/ticket/TicketWriterContext'
import { useGeckoNotifications } from 'features/notifications'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  reconcileZoneSnapshot,
  useCofData,
  useConfidenceFromScreening,
  ZONE_FIELD_IDS,
  ZoneAnalysisConfig,
  ZoneChangeGuard,
  ZoneParams,
  zoneParamsSchema,
} from './zoneAnalysisApi'
import { validateZoneGeometryChanges } from './zoneGeometryValidation'
import {
  patchZoneById,
  removeZoneById,
  replaceImageZoneProposals,
} from './zoneMutations'
import {
  latestZoneListOrigin,
  reconcileZoneOriginsAfterRefresh,
  trackZoneMutation,
  type ZoneOriginTracking,
} from './zoneOriginTracking'
import {
  captureZoneSave,
  hasDuplicateStoredZoneIds,
  hasEmptyStoredZoneId,
  isZoneDirty as zoneIsDirty,
  matchesPersistedZoneSnapshot,
  type PersistedZoneSnapshot,
  prepareZoneFieldSave,
  prepareZonesForSave,
  readZoneFieldSnapshot,
  reconcileZoneDrafts,
  reconcileZoneSaveSnapshot,
  stageZoneSnapshot,
  type ZoneSnapshot,
} from './zoneSave'

interface ZoneDraftState extends ZoneOriginTracking {
  savedZones: readonly ZoneParams[]
  zones: ZoneParams[]
  savedJustification: string | undefined
  justification: string | undefined
  justificationOrigin: FieldOrigin | undefined
  identityConflict: PersistedZoneSnapshot | null
}

interface ZoneSaveError {
  zoneId?: string
  message: string
}

function hasDraftChanges(state: ZoneDraftState): boolean {
  return (
    !isEqual(
      state.zones.filter((zone) => !zone.ai_suggested),
      state.savedZones
    ) || state.justification !== state.savedJustification
  )
}

function trackZoneStateMutation(
  state: ZoneDraftState,
  zones: ZoneParams[]
): ZoneDraftState {
  return {
    ...state,
    zones,
    ...trackZoneMutation(
      {
        zoneOrigins: state.zoneOrigins,
        zoneMutationOrder: state.zoneMutationOrder,
      },
      state.zones,
      zones
    ),
  }
}

type ZoneAnalysisDataContextValue = ReturnType<typeof useZoneDataManagement> & {
  // Zone analysis only consumes the unit/model/inspection surface — pick
  // those fields explicitly so the context isn't broken when
  // `useLibraAssetModelData` is widened for unrelated callers (e.g. the
  // asset-workflow tab layer added `inspectionEntries`, `forceRefresh`,
  // etc.).
  assetModelData: Pick<
    ReturnType<typeof useLibraAssetModelData>,
    | 'unit'
    | 'assetModel'
    | 'siteName'
    | 'components'
    | 'inspections'
    | 'componentDataFrames'
  >
  config: ZoneAnalysisConfig
  confidence: 'low' | 'med' | 'high' // From NII Screening
}

const ZoneAnalysisDataContext = createContext<ZoneAnalysisDataContextValue>({
  assetModelData: {
    unit: undefined,
    assetModel: undefined,
    siteName: '',
    components: [],
    inspections: [],
    componentDataFrames: [],
  },
  zones: [],
  setZones: () => {},
  cof: { level: 'medium', justification: undefined },
  zoneBreakdownJustification: undefined,
  setZoneBreakdownJustification: () => {},
  applyAiZoneBreakdownJustification: () => {},
  config: { zoneFieldName: ZONE_FIELD_IDS.ZONES },
  confidence: 'med',
  hasChanges: false,
  hasPendingChanges: () => false,
  isZoneDirty: () => false,
  saveZone: async () => {},
  doSave: async () => {},
  resetChanges: () => {},
  isLoading: false,
  isSaving: false,
  savingZoneId: undefined,
  saveError: null,
  addZone: () => {},
  changeZone: () => {},
  removeZone: () => {},
  confirmProposal: () => {},
  replaceImageProposals: () => {},
  drawingResetKey: 0,
})

const identityConflictMessage =
  'A legacy zone identity is ambiguous, so no save was made. Reset discards unsaved changes and loads the latest ticket data; if IDs are still missing or duplicated, have the stored zone IDs corrected before saving.'

function hasLocalDraftChanges(state: ZoneDraftState): boolean {
  return (
    !isEqual(state.zones, state.savedZones) ||
    state.justification !== state.savedJustification
  )
}

function useZoneDataManagement(fieldName: string) {
  const { readOnly, isLoading: isLoadingTicketData } = useTicketAccess()
  const { committedTicket, enqueue } = useTicketWriter()
  const { errorNotification, successNotification } = useGeckoNotifications()
  const rawTicketZones = committedTicket.fieldValues[fieldName]?.value
  const parsedTicketZones = useMemo(
    () =>
      rawTicketZones !== undefined
        ? zoneParamsSchema.array().safeParse(rawTicketZones)
        : { success: true as const, data: [] },
    [rawTicketZones]
  )
  const ticketZones = useMemo(
    () => (parsedTicketZones.success ? parsedTicketZones.data : []),
    [parsedTicketZones]
  )
  const { isLoading: isLoadingCofData, cof } = useCofData()
  const ticketJustificationValue =
    committedTicket.fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]?.value
  const ticketJustification =
    typeof ticketJustificationValue === 'string'
      ? ticketJustificationValue
      : undefined
  const initialZoneSnapshot = useMemo<ZoneSnapshot>(
    () => ({ zones: ticketZones, rawZones: rawTicketZones }),
    [rawTicketZones, ticketZones]
  )
  // Keeps generated legacy IDs stable; acknowledged values remain writer-owned.
  const zoneIdentityAnchorRef = useRef(initialZoneSnapshot)

  const [draftState, setDraftState] = useState<ZoneDraftState>(() => ({
    savedZones: ticketZones,
    zones: ticketZones,
    zoneOrigins: new Map(),
    zoneMutationOrder: 0,
    savedJustification: ticketJustification,
    justification: ticketJustification,
    justificationOrigin: undefined,
    identityConflict: null,
  }))
  const draftStateRef = useRef(draftState)
  const ticketIdRef = useRef(committedTicket.id)
  ticketIdRef.current = committedTicket.id
  const notifiedInvalidZonesRef = useRef<{
    ticketId: string
    rawZones: unknown
  } | null>(null)
  const savingRef = useRef(false)
  const mountedRef = useRef(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savingZoneId, setSavingZoneId] = useState<string>()
  const [saveError, setSaveError] = useState<ZoneSaveError | null>(null)
  const [drawingResetKey, setDrawingResetKey] = useState(0)

  useEffect(() => {
    if (parsedTicketZones.success) {
      notifiedInvalidZonesRef.current = null
      return
    }
    const previous = notifiedInvalidZonesRef.current
    if (
      previous?.ticketId === committedTicket.id &&
      isEqual(previous.rawZones, rawTicketZones)
    ) {
      return
    }
    notifiedInvalidZonesRef.current = {
      ticketId: committedTicket.id,
      rawZones: rawTicketZones,
    }
    errorNotification(`Invalid zone data in field: ${fieldName}`)
  }, [
    committedTicket.id,
    errorNotification,
    fieldName,
    parsedTicketZones,
    rawTicketZones,
  ])

  const commitState = useCallback(
    (update: (state: ZoneDraftState) => ZoneDraftState) => {
      const next = update(draftStateRef.current)
      draftStateRef.current = next
      setDraftState(next)
    },
    []
  )

  const updateZones = useCallback(
    (
      update: (zones: ZoneParams[]) => ZoneParams[],
      forceValidateIds: readonly string[] = []
    ) => {
      const current = draftStateRef.current.zones
      const next = update(current)
      if (isEqual(next, current)) return

      const geometryError = validateZoneGeometryChanges({
        baselineZones: current,
        proposedZones: next,
        forceValidateIds,
      })
      if (geometryError) {
        errorNotification(geometryError)
        return
      }

      commitState((state) => trackZoneStateMutation(state, next))
    },
    [commitState, errorNotification]
  )

  const addZone = useCallback(
    (zone: ZoneParams) =>
      updateZones((current) =>
        current.some((item) => item.id === zone.id)
          ? current
          : [...current, zone]
      ),
    [updateZones]
  )

  const changeZone = useCallback(
    (zoneId: string, updates: Partial<ZoneParams>, guard?: ZoneChangeGuard) =>
      updateZones((current) => patchZoneById(current, zoneId, updates, guard)),
    [updateZones]
  )

  const removeZone = useCallback(
    (zoneId: string) =>
      updateZones((current) => removeZoneById(current, zoneId)),
    [updateZones]
  )

  const confirmProposal = useCallback(
    (proposal: ZoneParams) =>
      updateZones(
        (current) => {
          const zone = current.find((item) => item.id === proposal.id)
          return zone?.ai_suggested
            ? current.map((item) =>
                item.id === proposal.id
                  ? { ...item, ai_suggested: false }
                  : item
              )
            : current
        },
        [proposal.id]
      ),
    [updateZones]
  )

  const replaceImageProposals = useCallback(
    (imageId: string, proposals: ZoneParams[]) =>
      commitState((state) =>
        trackZoneStateMutation(
          state,
          replaceImageZoneProposals(state.zones, imageId, proposals)
        )
      ),
    [commitState]
  )

  const prepareLatestSnapshot = useCallback(
    (
      latest: LibraTicket,
      identityBaseline: ZoneSnapshot
    ): ReturnType<typeof readZoneFieldSnapshot> => {
      const prepared = readZoneFieldSnapshot(
        latest.fieldValues[fieldName],
        identityBaseline
      )
      const rawJustification =
        latest.fieldValues[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]?.value
      const justification =
        typeof rawJustification === 'string' ? rawJustification : undefined
      const snapshot: PersistedZoneSnapshot = {
        ...prepared.snapshot,
        justification,
      }

      if (prepared.hasAmbiguousLegacyChange) {
        commitState((current) => ({
          ...current,
          identityConflict: current.identityConflict ?? snapshot,
        }))
        throw new Error(identityConflictMessage)
      }
      if (!prepared.isValid) {
        throw new Error(
          'Saved zone data is invalid. Reload the ticket before saving.'
        )
      }

      return prepared
    },
    [commitState, fieldName]
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  useEffect(() => {
    const anchor = zoneIdentityAnchorRef.current
    if (!parsedTicketZones.success) return
    const reconciled = reconcileZoneSnapshot(
      anchor.rawZones,
      anchor.zones,
      rawTicketZones,
      parsedTicketZones.data
    )
    const incomingSnapshot: ZoneSnapshot = {
      rawZones: rawTicketZones,
      zones: reconciled.zones,
    }
    const incomingJustification = ticketJustification
    const incoming: PersistedZoneSnapshot = {
      zones: incomingSnapshot.zones,
      justification: incomingJustification,
      rawZones: incomingSnapshot.rawZones,
    }
    if (
      matchesPersistedZoneSnapshot(
        {
          ...anchor,
          justification: draftStateRef.current.savedJustification,
        },
        incoming
      )
    ) {
      return
    }
    if (savingRef.current) return

    zoneIdentityAnchorRef.current = {
      zones: incoming.zones,
      rawZones: incoming.rawZones,
    }
    const hasAmbiguousIdentity = reconciled.hasAmbiguousLegacyChange
    const current = draftStateRef.current
    if (current.identityConflict) {
      commitState((state) => ({
        ...state,
        identityConflict: incoming,
      }))
      return
    }
    if (hasAmbiguousIdentity && hasLocalDraftChanges(current)) {
      commitState((state) => ({
        ...state,
        identityConflict: incoming,
      }))
      setSaveError({ message: identityConflictMessage })
      errorNotification(identityConflictMessage)
      return
    }
    commitState((state) => {
      const zones = reconcileZoneDrafts(
        state.savedZones,
        state.zones,
        incoming.zones
      )
      const justification =
        state.justification === state.savedJustification
          ? incomingJustification
          : state.justification
      return {
        ...state,
        savedZones: incoming.zones,
        zones,
        zoneOrigins: reconcileZoneOriginsAfterRefresh(
          state.zoneOrigins,
          zones,
          state.savedZones,
          incoming.zones
        ),
        savedJustification: incomingJustification,
        justification,
        justificationOrigin:
          justification === incomingJustification
            ? undefined
            : state.justificationOrigin,
      }
    })
  }, [
    committedTicket.id,
    fieldName,
    parsedTicketZones,
    rawTicketZones,
    ticketJustification,
    isSaving,
    commitState,
    errorNotification,
  ])

  const setZones = useCallback<Dispatch<SetStateAction<ZoneParams[]>>>(
    (update) => {
      commitState((current) =>
        trackZoneStateMutation(
          current,
          typeof update === 'function' ? update(current.zones) : update
        )
      )
    },
    [commitState]
  )

  const setZoneBreakdownJustification = useCallback<
    Dispatch<SetStateAction<string | undefined>>
  >(
    (update) => {
      commitState((current) => {
        const justification =
          typeof update === 'function' ? update(current.justification) : update
        return {
          ...current,
          justification,
          justificationOrigin:
            justification === current.savedJustification ? undefined : 'human',
        }
      })
    },
    [commitState]
  )

  const applyAiZoneBreakdownJustification = useCallback(
    (justification: string) => {
      commitState((current) => {
        return {
          ...current,
          justification,
          justificationOrigin:
            justification === current.savedJustification ? undefined : 'llm',
        }
      })
    },
    [commitState]
  )

  const hasChanges = useMemo(() => hasDraftChanges(draftState), [draftState])
  const hasPendingChanges = useCallback(
    () => hasDraftChanges(draftStateRef.current),
    []
  )
  const isZoneDirty = useCallback(
    (zoneId: string) =>
      zoneIsDirty(draftState.zones, draftState.savedZones, zoneId),
    [draftState]
  )

  const saveChanges = useCallback(
    async (zoneId?: string) => {
      const submission = draftStateRef.current
      const selectedSnapshot = zoneIdentityAnchorRef.current
      let capture: ReturnType<typeof captureZoneSave> | undefined
      if (zoneId !== undefined) {
        try {
          const currentSnapshot = stageZoneSnapshot(
            selectedSnapshot.rawZones,
            submission.savedZones,
            submission.zones,
            submission
          )
          if (!currentSnapshot) {
            throw new Error(identityConflictMessage)
          }
          capture = captureZoneSave(
            currentSnapshot.snapshot,
            { ...selectedSnapshot, zones: submission.savedZones },
            submission,
            zoneId
          )
        } catch (error) {
          const message =
            error instanceof Error
              ? error.message
              : 'This zone is no longer available. Refresh and retry.'
          setSaveError({ zoneId, message })
          errorNotification(message)
          throw error
        }
      }

      const identityBaseline = capture?.savedSnapshot ?? selectedSnapshot
      const submittedZones: readonly ZoneParams[] = submission.zones.filter(
        (zone) => !zone.ai_suggested
      )
      const submittedMutationOrder = submission.zoneMutationOrder
      const submittedJustification = submission.justification
      const submittedJustificationOrigin = submission.justificationOrigin
      const submittedZoneListOrigin = latestZoneListOrigin(
        submission.zoneOrigins
      )
      const persistedJustification = submittedJustification ?? ''
      let pageSubmittedSnapshot = identityBaseline
      const pageWritesZoneField =
        !capture &&
        (!isEqual(submittedZones, identityBaseline.zones) ||
          identityBaseline.zones.length > 0 ||
          identityBaseline.rawZones !== undefined)

      if (savingRef.current) {
        const error = new Error(
          'Another save is in progress. Wait for it to finish before saving again.'
        )
        errorNotification(error.message)
        throw error
      }
      if (draftStateRef.current.identityConflict) {
        const error = new Error(identityConflictMessage)
        setSaveError({ zoneId, message: error.message })
        errorNotification(error.message)
        throw error
      }
      if (readOnly) {
        const error = new Error('This ticket is read-only and cannot be saved.')
        errorNotification(error.message)
        throw error
      }

      const savingTicketId = committedTicket.id
      savingRef.current = true
      setIsSaving(true)
      setSavingZoneId(zoneId)
      setSaveError(null)

      try {
        const applied = await enqueue(
          (latest) => {
            if (latest.id !== savingTicketId) {
              throw new Error('The ticket changed before this save could run.')
            }
            const prepared = prepareLatestSnapshot(latest, identityBaseline)
            if (capture) {
              const preparedZone = prepareZoneFieldSave(
                latest.fieldValues[fieldName],
                capture,
                prepared
              )
              return {
                fieldValues: buildFieldValuesPatch(
                  latest.fieldValues,
                  { kind: 'ticket' },
                  { [fieldName]: preparedZone }
                ),
              }
            }

            const latestRawZones = prepared.snapshot.rawZones
            if (hasEmptyStoredZoneId(latestRawZones)) {
              throw new Error(
                'Saved zones contain an empty stored ID. Correct the stored zone data before using page Save.'
              )
            }
            if (hasDuplicateStoredZoneIds(latestRawZones)) {
              throw new Error(
                'Saved zones contain duplicate stored IDs. Have the stored IDs corrected before using page Save.'
              )
            }
            const savedZones = pageWritesZoneField
              ? prepareZonesForSave({
                  rawZones: latestRawZones,
                  previousZones: submission.savedZones,
                  latestZones: prepared.snapshot.zones,
                  proposedZones: submittedZones,
                })
              : []
            if (pageWritesZoneField) {
              const geometryError = validateZoneGeometryChanges({
                baselineZones: prepared.snapshot.zones,
                proposedZones: submittedZones,
              })
              if (geometryError) throw new Error(geometryError)
              pageSubmittedSnapshot = {
                rawZones: savedZones,
                zones: submittedZones,
              }
            } else {
              pageSubmittedSnapshot = prepared.snapshot
            }

            const values = applyOriginOption(
              {
                ...(pageWritesZoneField
                  ? { [fieldName]: { value: savedZones } }
                  : {}),
                [ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]: {
                  value: persistedJustification,
                },
              },
              {
                origin: {
                  ...(pageWritesZoneField
                    ? { [fieldName]: submittedZoneListOrigin }
                    : {}),
                  [ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]:
                    submittedJustificationOrigin,
                },
              }
            )
            return {
              fieldValues: buildFieldValuesPatch(
                latest.fieldValues,
                { kind: 'ticket' },
                values
              ),
            }
          },
          { applyInteractionSideEffects: true }
        )
        if (applied === null) {
          throw new Error('The save was not applied. Retry the save.')
        }
        if (!mountedRef.current || ticketIdRef.current !== savingTicketId) {
          throw new Error('The ticket changed before this save completed.')
        }

        const appliedZoneField = applied.fieldValues?.[fieldName]
        const acknowledgedField =
          !capture &&
          !pageWritesZoneField &&
          appliedZoneField?.value === undefined
            ? undefined
            : appliedZoneField
        if (!acknowledgedField && (capture || pageWritesZoneField)) {
          throw new Error(
            'The applied save result did not contain valid zone data. Retry the save.'
          )
        }
        const acknowledged = readZoneFieldSnapshot(
          acknowledgedField,
          capture ? identityBaseline : pageSubmittedSnapshot
        )
        if (!acknowledged.isValid) {
          throw new Error(
            'The applied save result did not contain valid zone data. Retry the save.'
          )
        }
        const rawJustification =
          applied.fieldValues?.[ZONE_FIELD_IDS.BREAKDOWN_JUSTIFICATION]?.value
        const savedJustification =
          typeof rawJustification === 'string' ? rawJustification : undefined
        const acknowledgedSnapshot: PersistedZoneSnapshot = {
          ...acknowledged.snapshot,
          justification: savedJustification,
        }
        commitState((current) => {
          const next = reconcileZoneSaveSnapshot({
            currentFieldExists: acknowledgedField !== undefined,
            currentRawZones: acknowledgedSnapshot.rawZones,
            currentDraftZones: current.zones,
            appliedRawZones: acknowledgedSnapshot.rawZones,
            appliedFieldExists: acknowledgedField !== undefined,
            originTracking: current,
            scope: capture
              ? { kind: 'zone', capture }
              : {
                  kind: 'page',
                  submittedSnapshot: pageSubmittedSnapshot,
                  comparisonBaseline: submittedZones,
                  submittedMutationOrder,
                },
          })
          const acknowledgedJustification =
            savedJustification ?? current.savedJustification
          const justificationBaseline = capture
            ? current.savedJustification
            : submittedJustification
          const justification =
            current.justification === justificationBaseline
              ? acknowledgedJustification
              : current.justification
          zoneIdentityAnchorRef.current = next.savedSnapshot
          return {
            ...current,
            ...next.originTracking,
            savedZones: next.savedSnapshot.zones,
            zones: next.currentSnapshot.zones,
            savedJustification: acknowledgedJustification,
            justification,
            justificationOrigin:
              justification === acknowledgedJustification
                ? undefined
                : current.justificationOrigin,
          }
        })
        setSaveError(null)
        successNotification(
          capture
            ? `Zone ${capture.selectedZone.title ?? ''} saved.`
            : 'NII zone changes saved.'
        )
      } catch (error) {
        if (mountedRef.current && ticketIdRef.current === savingTicketId) {
          const message =
            error instanceof Error
              ? error.message
              : 'The save failed. Retry the save.'
          setSaveError({ zoneId, message })
          errorNotification(message)
        }
        throw error
      } finally {
        savingRef.current = false
        if (mountedRef.current && ticketIdRef.current === savingTicketId) {
          setIsSaving(false)
          setSavingZoneId(undefined)
        }
      }
    },
    [
      enqueue,
      errorNotification,
      fieldName,
      committedTicket.id,
      readOnly,
      prepareLatestSnapshot,
      commitState,
      successNotification,
    ]
  )

  const doSave = useCallback(() => saveChanges(), [saveChanges])
  const saveZone = useCallback(
    (zoneId: string) => saveChanges(zoneId),
    [saveChanges]
  )

  const resetChanges = useCallback(() => {
    commitState((current) => {
      const savedZones = current.identityConflict?.zones ?? current.savedZones
      const savedJustification = current.identityConflict
        ? current.identityConflict.justification
        : current.savedJustification
      if (current.identityConflict) {
        zoneIdentityAnchorRef.current = {
          zones: current.identityConflict.zones,
          rawZones: current.identityConflict.rawZones,
        }
      }
      return {
        ...current,
        savedZones,
        zones: [...savedZones],
        zoneOrigins: new Map(),
        savedJustification,
        justification: savedJustification,
        justificationOrigin: undefined,
        identityConflict: null,
      }
    })
    setSaveError(null)
    setDrawingResetKey((current) => current + 1)
  }, [commitState])

  return useMemo(
    () => ({
      zones: draftState.zones,
      setZones,
      addZone,
      changeZone,
      removeZone,
      confirmProposal,
      replaceImageProposals,
      cof,
      zoneBreakdownJustification: draftState.justification,
      setZoneBreakdownJustification,
      applyAiZoneBreakdownJustification,
      doSave,
      saveZone,
      hasChanges,
      hasPendingChanges,
      isZoneDirty,
      resetChanges,
      drawingResetKey,
      isLoading: isLoadingTicketData || isLoadingCofData || isSaving,
      isSaving,
      savingZoneId,
      saveError,
    }),
    [
      draftState,
      setZones,
      addZone,
      changeZone,
      removeZone,
      confirmProposal,
      replaceImageProposals,
      cof,
      setZoneBreakdownJustification,
      applyAiZoneBreakdownJustification,
      doSave,
      saveZone,
      hasChanges,
      hasPendingChanges,
      isZoneDirty,
      resetChanges,
      drawingResetKey,
      isLoadingTicketData,
      isLoadingCofData,
      isSaving,
      savingZoneId,
      saveError,
    ]
  )
}

export const ZoneAnalysisDataProvider: FC<
  PropsWithChildren<{ config: ZoneAnalysisConfig }>
> = ({ children, config }) => {
  const assetModelDataRaw = useLibraAssetModelData()
  const assetModelData = useMemo(
    () => ({
      unit: assetModelDataRaw.unit,
      assetModel: assetModelDataRaw.assetModel,
      siteName: assetModelDataRaw.siteName,
      components: assetModelDataRaw.components,
      inspections: assetModelDataRaw.inspections,
      componentDataFrames: assetModelDataRaw.componentDataFrames,
    }),
    [
      assetModelDataRaw.unit,
      assetModelDataRaw.assetModel,
      assetModelDataRaw.siteName,
      assetModelDataRaw.components,
      assetModelDataRaw.inspections,
      assetModelDataRaw.componentDataFrames,
    ]
  )

  const zoneData = useZoneDataManagement(config.zoneFieldName)

  const confidence = useConfidenceFromScreening(config.confidenceFieldName)

  const value = useMemo(
    () => ({
      assetModelData,
      ...zoneData,
      config,
      confidence,
      isLoading: zoneData.isLoading || assetModelDataRaw.isLoading,
    }),
    [assetModelData, assetModelDataRaw.isLoading, config, confidence, zoneData]
  )

  return (
    <ZoneAnalysisDataContext.Provider value={value}>
      {children}
    </ZoneAnalysisDataContext.Provider>
  )
}

export const useZoneAnalysisData = () => {
  const context = useContext(ZoneAnalysisDataContext)
  if (!context) {
    throw new Error(
      'useZoneAnalysisData must be used within ZoneAnalysisDataProvider'
    )
  }
  return context
}
